<template>
    <div class="assignment-answer">
        <div class="assignment-answer-file-upload">
            <div
                class="loading"
                v-if="loading">
                <i class="fa fa-spinner fa-spin"></i>
            </div>

            <div
                class="file-upload"
                v-else>
                <div
                    class="not-found with-header empty-list"
                    v-if="!this.files.length">
                    <div
                        class="btn-add-circle icon remove-icon-margin"
                        v-if="canAnswer"
                        @click="$refs.fileUpload.click()"></div>
                    <div
                        class="btn-exclamation-circle icon remove-icon-margin"
                        v-else></div>
                    <div class="alert-notice">
                        {{ canAnswer ? $t('assignment.no_files') : $t('assignment.none') }}
                        {{ $t('assignment.files_added') }}
                    </div>
                </div>

                <div
                    class="files"
                    v-else>
                    <div
                        class="photos2"
                        v-if="imageFiles.length">
                        <div class="image-group">
                            <LightBox
                                ref="lightbox"
                                :media="images"
                                :showLightBox="false"></LightBox>
                            <b-card-group columns>
                                <b-card
                                    v-for="(file, index) in imageFiles"
                                    :key="index"
                                    :img-src="file.file.path"
                                    @click="onImageClick(index)">
                                    <b-card-text
                                        class="image-controls"
                                        text-tag="div">
                                        <div class="title">
                                            {{ file.file.name }}
                                            <div class="file-size">
                                                {{ file.file.size }}
                                            </div>
                                        </div>
                                        <b-button
                                            class="btn-download btn-download-image fa-fw"
                                            v-b-tooltip.hover
                                            variant="primary-icon-only"
                                            @click.prevent.stop="downloadFile(file.id)"
                                            :title="$t('common.download')">
                                        </b-button>
                                        <b-button
                                            class="btn-remove btn-image-delete fa-fw"
                                            v-b-tooltip.hover
                                            v-if="canAnswer"
                                            variant="danger-icon-only"
                                            :title="$t('common.delete')"
                                            @click.prevent.stop="removeFile(file)">
                                        </b-button>
                                    </b-card-text>
                                </b-card>
                            </b-card-group>
                        </div>
                    </div>

                    <div
                        class="regular-files"
                        v-if="regularFiles.length > 0">
                        <div class="component-title">
                            <h5 class="title">{{ $t('common.attachments') }}</h5>
                        </div>
                        <div
                            class="assignment-answer-file-uploads"
                            v-for="file in regularFiles">
                            <div
                                class="audio-file file"
                                v-if="isAudio(file.file)">
                                <audio
                                    controls
                                    :ref="`audio-file-${file.id}`">
                                    <source
                                        :src="file.file.public_path"
                                        type="audio/mpeg" />
                                    {{ $t('assignment.browser_doesnt_support_this_audio_element') }}
                                </audio>
                                <b-button
                                    class="btn-remove btn-remove-audio-file"
                                    v-if="canAnswer"
                                    v-b-tooltip.hover
                                    :title="$t('common.delete')"
                                    variant="danger-icon-only"
                                    @click="removeFile(file)">
                                </b-button>
                            </div>

                            <div
                                class="file"
                                v-else>
                                <div
                                    class="icon icon-type"
                                    :class="getFileType(file.file.extensions)"></div>
                                <div class="title">
                                    <b-button
                                        class="btn-preview-file"
                                        v-if="common.canPreviewFile(file.file.extension)"
                                        variant="link"
                                        @click="previewFile(file.file)">
                                        {{ file.file.full_name }}
                                    </b-button>
                                    <p
                                        class="title-text"
                                        v-else>
                                        {{ file.file.full_name }}
                                    </p>
                                    <div class="file-size text-muted">
                                        {{ file.file.size }}
                                    </div>
                                </div>
                                <div class="assignment-answer-file-upload-edit">
                                    <b-button
                                        class="btn-download btn-file-download"
                                        v-b-tooltip.hover
                                        variant="primary-icon-only"
                                        @click="downloadFile(file.id)"
                                        :download="file.file.public_path"
                                        :title="$t('common.download')">
                                    </b-button>

                                    <div v-if="partIn">
                                        <b-button
                                            class="btn-file-check btn-plagiarism"
                                            v-b-tooltip.hover
                                            v-if="partIn.hasAnswer() && !$authUser.isStudent()"
                                            v-b-modal="'assignment-show-answer-file-plagiarism-modal-' + file.id"
                                            :title="$t('assignment.plagiarism')"
                                            variant="primary-icon-only">
                                        </b-button>
                                        <design-2021-assignment-show-answer-file-plagiarism-modal-component
                                            v-if="partIn.hasAnswer() && !$authUser.isStudent()"
                                            :assignment-answer-file-id="file.id"
                                            :answer-id="partIn.getAnswer().id">
                                        </design-2021-assignment-show-answer-file-plagiarism-modal-component>
                                    </div>

                                    <b-button
                                        class="btn-remove"
                                        v-if="canAnswer"
                                        v-b-tooltip.hover
                                        :title="$t('common.delete')"
                                        variant="danger-icon-only"
                                        @click="removeFile(file)">
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="videos"
                        v-for="file in videoFiles"
                        v-if="videoFiles.length > 0">
                        <div class="video">
                            <design-2021-apprentice-video-player-component :options="getSource(file.file)">
                            </design-2021-apprentice-video-player-component>
                            <div class="description">
                                <div class="content">
                                    <p class="file-name">{{ file.file.name }}</p>
                                    <p class="file-size">{{ file.file.size }}</p>
                                </div>

                                <b-button
                                    class="btn-remove btn-remove-video-file"
                                    v-b-tooltip.hover
                                    v-if="canAnswer && ($authUser.id === file.file.user_id || $authUser.isAdmin())"
                                    variant="danger-icon-only"
                                    :title="$t('common.delete')"
                                    @click="removeFile(file)">
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="upload-new-file"
            v-if="canAnswer && this.files.length >= 1">
            <b-button
                class="btn-file-upload"
                variant="primary"
                @click="$refs.fileUpload.click()"
                :disabled="loading">
                <i class="btn-upload"></i> {{ $t('assignment.upload_files') }}
            </b-button>
        </div>
        <input
            v-show="false"
            v-on:change="uploadFilesChanged"
            ref="fileUpload"
            type="file"
            multiple />
    </div>
</template>

<script>
import LightBox from 'vue-image-lightbox';

require('vue-image-lightbox/dist/vue-image-lightbox.min.css');
import { ords } from '../../../../../../app';

export default {
    name: 'design-221-assignment-answer-upload-component',
    props: ['partIn', 'canAnswer', 'projectId'],
    components: {
        LightBox,
    },
    data() {
        return {
            loading: false,
        };
    },
    computed: {
        files() {
            return this.partIn.answers.length ? this.partIn.answers[0].files : [];
        },
        imageFiles() {
            let images = [];
            var self = this;
            this.files.forEach(function (file) {
                if (self.isImage(file.file)) {
                    images.push(file);
                }
            });

            return images;
        },
        images() {
            let images = [];
            var self = this;
            this.files.forEach(function (file) {
                if (self.isImage(file.file)) {
                    images.push({ thumb: file.file.path, src: file.file.path, caption: '' });
                }
            });

            return images;
        },
        regularFiles() {
            let files = [];
            var self = this;
            this.files.forEach(function (file) {
                if (!self.isImage(file.file) && !self.isVideo(file.file)) {
                    files.push(file);
                }
            });

            return files;
        },

        videoFiles() {
            let videos = [];
            var self = this;
            this.files.forEach(function (file) {
                if (self.isVideo(file.file)) {
                    videos.push(file);
                }
            });

            return videos;
        },
    },
    methods: {
        onImageClick(index) {
            this.$refs.lightbox.showImage(index);
        },
        uploadFilesChanged() {
            this.loading = true;
            const files = this.$refs.fileUpload.files;

            if (!this.partIn.hasAnswer()) {
                this.createAnswer(files);
            } else {
                this.createFiles(files);
            }
        },
        numImages() {
            let count = 0;
            for (var i = 0; i < this.files.length; i++) {
                if (this.isImage(this.files[i].file)) {
                    count++;
                }
            }

            return count;
        },
        numRegularFiles() {
            return this.files.length - this.numImages();
        },
        createAnswer(files) {
            let data = new FormData();
            data.append('part_id', this.partIn.id);
            data.append('project_id', this.projectId);

            let count = 0;
            for (const file of files) {
                data.append('value[' + count + ']', file);
                count++;
            }

            const headers = { 'Content-Type': 'multipart/form-data' };

            this.axios.post(this.routes.assignmentAnswer(), data, { headers: headers }).then(
                (response) => {
                    this.partIn.addRelation('answers', response.data);
                    this.loading = false;
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        createFiles(files) {
            let data = new FormData();
            let count = 0;
            for (const file of files) {
                data.append('files[' + count + ']', file);
                count++;
            }
            const headers = { 'Content-Type': 'multipart/form-data' };

            this.axios
                .post(this.routes.assignmentAnswerFiles(this.partIn.getAnswer().id), data, { headers: headers })
                .then(
                    (response) => {
                        ords.hold();
                        for (const file of response.data) {
                            this.partIn.getAnswer().addRelation('files', file);
                        }
                        ords.continue();

                        this.loading = false;
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
        },
        removeFile(file) {
            this.alert
                .confirm(this.$t('assignment.delete_file'), this.$t('assignment.confirm_delete_file'))
                .then((confirm) => {
                    if (!confirm) {
                        return;
                    }

                    this.alert.saving();
                    this.axios.delete(this.routes.assignmentAnswerFileRemove(file.id)).then(
                        (response) => {
                            file.remove();
                            this.alert.success();
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                });
        },
        isImage(file) {
            const extenions = [
                'jpg',
                'JPG',
                'jpeg',
                'JPEG',
                'png',
                'PNG',
                'gif',
                'GIF',
                'tiff',
                'TIFF',
                'bmp',
                'BMP',
                'jif',
                'jfif',
            ];
            for (const ext of extenions) {
                if (ext === file.extension) {
                    return true;
                }
            }
            return false;
        },
        isVideo(file) {
            return (
                file.extension === 'mp4' ||
                file.extension === 'MP4' ||
                file.extension === 'mov' ||
                file.extension === 'MOV'
            );
        },
        isAudio(file) {
            return file.extension === 'mp3';
        },
        getSource(file) {
            let ext = file.extension;
            if (ext === 'ogv') {
                ext = 'ogg';
            }
            if (ext === 'mov' || ext === 'MOV') {
                ext = 'mp4';
            }

            return {
                responsive: true,
                autoplay: false,
                controls: true,
                sources: [
                    {
                        type: 'video/' + ext,
                        src: file.public_path,
                    },
                ],
            };
        },
        downloadFile(id) {
            window.open(this.routes.assignmentAnswerFileDownload(id), '_blank');
        },
        playAudio(fileId) {
            const name = 'audio-file-' + fileId;
            this.$refs[name][0].play();
        },

        getFileType(filetype) {
            switch (filetype) {
                case 'jpeg':
                case 'jpg':
                case 'png':
                case 'jif':
                case 'jfif':
                case 'gif':
                    return 'btn-image-type ';

                case 'pdf':
                    return 'btn-file-type ';

                case 'doc':
                case 'docx':
                    return 'btn-doc-type ';

                case 'ppt':
                case 'pptx':
                    return 'btn-doc-type ';

                case 'xls':
                case 'xlx':
                    return 'btn-doc-type ';

                case 'cda':
                case 'mp3':
                case 'oga':
                    return 'btn-audio-type ';

                case 'mov':
                case 'mpeg':
                case 'mkv':
                case 'ogv':
                case 'webm':
                case '3gp':
                    return 'btn-video-type ';

                default:
                    return 'btn-file-type ';
            }
        },

        previewFile(file) {
            this.$root.$refs.filePreview.show(file);
        },
    },
};
</script>

<style scoped lang="scss">
.alert-assignment-status {
    margin-bottom: 0;
}

.assignment-answer {
    margin-top: map-get($grid-gutter-spacing, 'twenty-one');

    .assignment-answer-file-upload {
        .loading {
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 2rem;
            color: $primary;
            padding-top: map-get($grid-gutter-spacing, 'twenty-one');
            padding-bottom: map-get($grid-gutter-spacing, 'twenty-one');
        }
    }

    .btn-file-upload {
        display: flex;
        align-items: center;
        column-gap: map-get($grid-gutter-spacing, 'eight');
    }
}

.photos2 {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');

    .card {
        cursor: pointer;
        border-radius: $border-radius-content;
        background-color: transparent;

        .card-body {
            padding: 0;
        }

        &:hover {
            img {
                opacity: 0.5;
            }

            .card-body {
                background-color: $white;
                border-radius: $border-radius-content;
            }
        }
    }

    .card-columns:not(:last-of-type) {
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
    }

    img {
        width: 100%;
        border-top-left-radius: $border-radius-content;
        border-top-right-radius: $border-radius-content;
    }
}

.image-controls {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    height: 100%;
    padding: map-get($grid-gutter-spacing, 'fifteen');

    .title {
        font-weight: $font-weight-bold;
        font-size: $base-font-size;
        line-height: 1;
    }

    .file-size {
        color: $text-muted-color;
        font-size: 0.75rem;
        margin-top: map-get($grid-gutter-spacing, 'eight');
    }

    .btn-download-image {
        margin-left: auto;
        margin-right: map-get($grid-gutter-spacing, 'twenty-one');
    }
}

.photos2:not(:last-child),
.videos:not(:last-child),
.regular-files:not(:last-child) {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.video {
    .description {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        flex-wrap: wrap;
        background-color: $white;
        padding: map-get($grid-gutter-spacing, 'eight');
        border-bottom-left-radius: 12px;
        border-bottom-right-radius: 12px;

        .content {
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
        }

        p {
            margin-bottom: 0;
            line-height: 1.3;
        }

        .file-name {
            font-weight: $font-weight-bold;
        }

        .file-size {
            font-size: 0.75rem;
            color: $text-muted-color;
        }
    }
}

.regular-files {
    .component-title {
        flex: 1 0 100%;
    }

    .assignment-answer-file-uploads {
        display: flex;
        justify-content: flex-start;
        align-content: center;
        font-size: 1.3125rem;

        .file {
            display: flex;
            justify-content: flex-start;
            flex-wrap: wrap;
            align-items: center;
            flex: 1;
        }

        .icon {
            align-self: flex-start;
            padding: 10px;
            background: $secondary-background-color;
            border-radius: $border-radius-content;
            color: $primary;
            border: 1px solid $primary;
        }

        .title {
            font-size: $base-font-size;
            font-family: $headings-font-family;
            font-weight: $font-weight-bold;
            margin-left: map-get($grid-gutter-spacing, 'ten');
            margin-right: map-get($grid-gutter-spacing, 'ten');
            align-self: center;
            word-break: break-word;
            width: fit-content;
            max-width: calc(100% - 70px);

            .file-size {
                font-size: 0.75rem;
                font-weight: $font-weight-normal;
            }
        }

        .download-file {
            display: flex;
            align-items: center;
            align-self: center;
            margin-left: auto;

            .btn-file-download {
                font-size: 1.3125rem;

                &:before {
                    margin-right: 0;
                }
            }
        }

        .assignment-answer-file-upload-edit {
            display: flex;
            align-items: center;
            align-content: center;
            margin-left: auto;

            .btn {
                font-size: 1.3125rem;
            }

            .btn,
            > div {
                &:not(:last-child) {
                    margin-right: map-get($grid-gutter-spacing, 'ten');
                }

                &:nth-last-child(2) {
                    margin-right: map-get($grid-gutter-spacing, 'fifteen');
                }
            }

            .btn-file-download {
                &:before {
                    margin-right: 0;
                }
            }
        }

        .audio-file {
            justify-content: space-between;

            .btn-remove-audio-file {
                font-size: 1.3125rem;
            }
        }

        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }
    }
}

.upload-new-file {
    margin-top: map-get($grid-gutter-spacing, 'forty-two');
}

.not-found {
    .icon {
        border: 1px solid $primary;
    }
}

@media (min-width: map-get($grid-breakpoints, 'sm')) {
    .photos2 {
        .card-columns {
            column-count: 2;
        }
    }

    .image-controls {
        flex-wrap: wrap;

        .title {
            flex: 1 0 100%;
            margin-bottom: map-get($grid-gutter-spacing, 'eight');
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    .photos2 {
        .card-columns {
            column-count: 3;
        }

        .card-body {
            .title {
                flex: 1 0 calc(100% - 76px);
            }
        }
    }

    .image-controls {
        flex-wrap: nowrap;

        .title {
            flex: 0 0 auto;
            margin-right: map-get($grid-gutter-spacing, 'ten');
            margin-bottom: 0;
        }
    }

    .video {
        .content {
            width: calc(50% - 30px);
        }
    }

    .regular-files {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        flex-wrap: wrap;

        .assignment-answer-file-uploads {
            flex: 1 0 calc(50% - 30px);
            max-width: calc(50% - 30px);

            .assignment-answer-file-upload-edit {
                .btn,
                > div {
                    &:nth-last-child(2) {
                        margin-right: map-get($grid-gutter-spacing, 'twenty-one');
                    }
                }
            }

            .audio-file {
                .btn-audio.icon {
                    display: flex;
                }

                .audio-controls {
                    width: calc(100% - 65px);
                    align-self: flex-start;
                    margin-left: map-get($grid-gutter-spacing, 'fifteen');
                }
            }

            &:not(:last-child) {
                margin-bottom: 0;
            }

            &:nth-child(odd) {
                margin-right: map-get($grid-gutter-spacing, 'fifteen');
            }

            &:not(:nth-last-child(1)):not(:nth-last-child(2)) {
                margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
            }
        }
    }
}
</style>
